/**
 * Wiedervorlagen (Fachmodul "crm").
 *
 * Die Arbeitsliste des Tages: offene Punkte nach Faelligkeit, vorbelegt
 * auf den angemeldeten Benutzer. Die Sachbearbeitung stellt hier dem
 * Aussendienst Rueckrufe ein; erledigt wird mit einem Klick.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api, withQuery } from '../api/client';
import type { ListResponse } from '../api/types';
import { useAuth } from '../auth';
import {
  ActivityDetailDialog,
  CreateActivityDialog,
  dueLabel,
  invalidateActivities,
  kindBadge,
  type ActivityRow,
} from '../components/CrmActivity';
import { PageHeader } from '../components/Layout';
import { useToast } from '../components/toast';
import { EmptyState, LoadingBlock } from '../components/ui';

export function CrmFollowUpsPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [view, setView] = useState<'me' | 'all'>('me');
  const [creating, setCreating] = useState(false);
  const [openRow, setOpenRow] = useState<ActivityRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['crm-activities', 'follow-ups', view],
    queryFn: () =>
      api.get<ListResponse<ActivityRow>>(
        withQuery('/api/crm/activities', {
          status: 'open',
          assignedTo: view === 'me' ? 'me' : '',
          limit: 200,
        }),
      ),
  });

  const complete = useMutation({
    mutationFn: (id: string) => api.patch(`/api/crm/activities/${id}`, { status: 'done' }),
    onSuccess: () => {
      invalidateActivities(queryClient);
      toast.success('Erledigt.');
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Die Aktion ist fehlgeschlagen.'),
  });

  return (
    <>
      <PageHeader
        title="Wiedervorlagen"
        actions={
          can('crm:write') ? (
            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
              Wiedervorlage einstellen
            </button>
          ) : undefined
        }
      />

      <div className="row" style={{ gap: 6, marginBottom: 14 }}>
        <button
          type="button"
          className={`btn btn-sm${view === 'me' ? ' btn-primary' : ''}`}
          onClick={() => setView('me')}
        >
          Meine
        </button>
        <button
          type="button"
          className={`btn btn-sm${view === 'all' ? ' btn-primary' : ''}`}
          onClick={() => setView('all')}
        >
          Alle Benutzer
        </button>
      </div>

      <div className="card">
        {isLoading || !data ? (
          <LoadingBlock />
        ) : data.items.length === 0 ? (
          <EmptyState
            title={view === 'me' ? 'Keine offenen Wiedervorlagen für Sie.' : 'Keine offenen Wiedervorlagen.'}
            hint={'Neue Punkte entstehen über „Wiedervorlage einstellen" oder aus der Kundenakte.'}
          />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th className="shrink">Fällig</th>
                  <th className="shrink">Art</th>
                  <th>Partner</th>
                  <th>Betreff</th>
                  {view === 'all' ? <th>Zuständig</th> : null}
                  <th className="shrink" />
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <tr key={row.id} onClick={() => setOpenRow(row)} style={{ cursor: 'pointer' }}>
                    <td>{dueLabel(row)}</td>
                    <td>{kindBadge(row.kind)}</td>
                    <td>
                      <Link
                        to={`${row.partnerIsCustomer ? '/kunden' : '/lieferanten'}/${row.partnerId}`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {row.partnerName}
                      </Link>
                    </td>
                    <td>{row.subject}</td>
                    {view === 'all' ? (
                      <td>{row.assignedToName ?? <span className="faint">—</span>}</td>
                    ) : null}
                    <td>
                      {can('crm:write') ? (
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={complete.isPending}
                          onClick={(event) => {
                            event.stopPropagation();
                            complete.mutate(row.id);
                          }}
                        >
                          Erledigen
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating ? <CreateActivityDialog onClose={() => setCreating(false)} /> : null}
      {openRow ? <ActivityDetailDialog activity={openRow} onClose={() => setOpenRow(null)} /> : null}
    </>
  );
}
